import categoriesAtom from "@/atoms/categories"
import { tenantTimezoneAtom } from "@/atoms/tenant-clock"
import { prizeForCode } from "@/domain/marketing/won-prize"
import { bagCouponView, evaluationSubtotalCents, type BagCoupon } from "@/lib/bag-coupon"
import { getMerchantId } from "@/lib/company-storage"
import { showToast } from "@/components/feedback"
import { detachBagCouponCode, forgetCouponCode } from "@/lib/bag-coupon"
import { evaluateBagCoupons, type BagEvaluation } from "@/lib/bag-coupon-source"
import { refusedCouponsMessages, unusableCouponCodes } from "@/lib/coupon-arrival"
import { buildEvaluationLines } from "@/lib/discount-evaluation/cart-lines"
import type { EvaluateCouponsRequest } from "@/lib/discount-evaluation/types"
import { attachedCouponCodes, couponCodeKey, savePendingCouponCodes, subscribeCouponStash } from "@/lib/pending-coupon"
import type { Cart } from "@/types"
import { atom, useAtomValue, useStore } from "jotai"
import { useEffect, useMemo, useSyncExternalStore } from "react"
import { useAuthStatus } from "./use-auth-status"
import { useCouponTerms } from "./use-coupon-terms"
import { useCouponWallet } from "./use-coupon-wallet"
import { useWonPrizes } from "./use-won-prizes"

const EVALUATION_DEBOUNCE_MS = 300

const PUBLIC_EVALUATION_DEBOUNCE_MS = 400

const CODE_SEPARATOR = "\n"

type BagSnapshot = { key: string; data: BagEvaluation | null }

/** The last verdict for the bag (null data when it failed), shared by the bag strip and the bag pill; never persisted. */
export const bagCouponSnapshotAtom = atom<BagSnapshot | null>(null)

/** The coupons riding on the bag, prizes excluded (CartPrizes shows those); `ready` once it is known which codes are prizes. */
export type BagCoupons = { coupons: BagCoupon[]; ready: boolean }

function dropUnusableCoupons(merchantId: string, data: BagEvaluation, codes: readonly string[]): void {
  const { refused, outranked } = unusableCouponCodes(data.evaluation, codes)
  for (const code of outranked) detachBagCouponCode(merchantId, code)
  for (const code of refused) forgetCouponCode(merchantId, code)
  for (const message of refusedCouponsMessages(data.evaluation, refused)) showToast.info(message)
}

const preselectedFor = new Set<string>()

/** Test seam: the next bag picks the best wallet coupon again, as on a fresh visit. */
export function resetWalletPreselectionForTests(): void {
  preselectedFor.clear()
}

function useAttachedCodes(merchantId: string | null): string[] {
  const joined = useSyncExternalStore(
    subscribeCouponStash,
    () => attachedCouponCodes(merchantId).join(CODE_SEPARATOR),
    () => "",
  )
  return joined ? joined.split(CODE_SEPARATOR) : []
}

/**
 * Every coupon code on this visit's order (stashed or kept, prizes left to
 * CartPrizes) as it stands on the bag: the verdict for the bag on screen
 * through `evaluateBagCoupons` (coupon-service with the session or its public
 * preview without one, the request checkout makes), debounced and cancelled
 * when the bag moves on, delivery fee unknown so 0, plus how far the coupon's
 * minimum order still is.
 */
export function useBagCoupons(cart: Cart): BagCoupons {
  const store = useStore()
  const authenticated = useAuthStatus()
  const categories = useAtomValue(categoriesAtom)
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const merchantId = getMerchantId()
  const attached = useAttachedCodes(merchantId)
  const won = useWonPrizes(authenticated)
  const ready = !authenticated || attached.length === 0 || won.status === "ready"
  const codes = ready && won.status === "ready" ? attached.filter((code) => !prizeForCode(won.prizes, code)) : ready ? attached : []
  const terms = useCouponTerms(merchantId, codes)
  const snapshot = useAtomValue(bagCouponSnapshotAtom)

  const attachedKey = attached.join(CODE_SEPARATOR)
  const lines = useMemo(() => buildEvaluationLines(cart, categories), [cart, categories])
  const request = useMemo<EvaluateCouponsRequest | null>(
    () =>
      attachedKey && lines.length > 0
        ? {
            couponCodes: attachedKey.split(CODE_SEPARATOR),
            lines,
            deliveryFeeCents: 0,
            cartBenefitDiscountCents: 0,
            deliveryBenefitDiscountCents: 0,
          }
        : null,
    [attachedKey, lines],
  )
  const codesKey = codes.join(CODE_SEPARATOR)
  const termsRead = authenticated ? "" : codes.map((code) => (code in terms ? "1" : "0")).join("")
  const key = request && codes.length > 0 ? JSON.stringify({ request, authenticated, termsRead }) : ""

  useEffect(
    function evaluateBagCouponsForThisBag() {
      if (!request || !key || !merchantId) return
      if (store.get(bagCouponSnapshotAtom)?.key === key) return
      const controller = new AbortController()
      const timer = window.setTimeout(() => {
        evaluateBagCoupons({ merchantId, request, authenticated, terms, now: new Date(), signal: controller.signal }).then(
          (data) => {
            store.set(bagCouponSnapshotAtom, { key, data })
            dropUnusableCoupons(merchantId, data, codesKey.split(CODE_SEPARATOR))
          },
          () => {
            if (!controller.signal.aborted) store.set(bagCouponSnapshotAtom, { key, data: null })
          },
        )
      }, authenticated ? EVALUATION_DEBOUNCE_MS : PUBLIC_EVALUATION_DEBOUNCE_MS)
      return () => {
        window.clearTimeout(timer)
        controller.abort()
      }
    },
    [request, key, merchantId, store, authenticated, terms, codesKey],
  )

  const wallet = useCouponWallet()
  const walletPick = ready && won.status === "ready" ? wallet.filter((code) => !prizeForCode(won.prizes, code)) : ready ? wallet : []
  const walletKey = walletPick.join(CODE_SEPARATOR)
  const preselect = merchantId && !attachedKey && walletKey && lines.length > 0 && !preselectedFor.has(merchantId)

  useEffect(
    function preselectBestWalletCoupon() {
      if (!preselect || !merchantId) return
      const controller = new AbortController()
      const couponCodes = walletKey.split(CODE_SEPARATOR)
      const body = { couponCodes, lines, deliveryFeeCents: 0, cartBenefitDiscountCents: 0, deliveryBenefitDiscountCents: 0 }
      const timer = window.setTimeout(() => {
        preselectedFor.add(merchantId)
        evaluateBagCoupons({ merchantId, request: body, authenticated, terms: {}, now: new Date(), signal: controller.signal }).then(
          ({ evaluation }) => {
            const { refused } = unusableCouponCodes(evaluation, couponCodes)
            for (const code of refused) forgetCouponCode(merchantId, code)
            const best = evaluation.applied.map((coupon) => coupon.code)
            const picked = couponCodes.filter((code) => best.some((applied) => couponCodeKey(applied) === couponCodeKey(code)))
            if (picked.length > 0) savePendingCouponCodes(picked, merchantId)
          },
          () => undefined,
        )
      }, PUBLIC_EVALUATION_DEBOUNCE_MS)
      return () => {
        window.clearTimeout(timer)
        controller.abort()
      }
    },
    [preselect, merchantId, walletKey, lines, authenticated],
  )

  const fresh = snapshot?.key === key
  const verdict = snapshot?.data ? { evaluation: snapshot.data.evaluation, fresh } : null
  const unconfirmed = !request || (fresh && snapshot?.data === null)
  const subtotalCents = evaluationSubtotalCents(lines)
  const now = new Date()
  const coupons = codes.map((code) =>
    bagCouponView({ code, terms: terms[code], verdict, unconfirmed, subtotalCents, cart, now, timeZone }),
  )
  return { coupons, ready }
}
